import React from 'react'
import { I18n } from '@front/volcanion'

import { Box, Typography, Grid, Button, Link } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'

import { TextComponent, TextField } from '@front/squirtle'

const Title = ({ billing_status, billing_color, stripeLink }) =>
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', p: 1 }}>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography variant='h5'>{I18n.t('billing.label', { count: 1 })}</Typography>
      {!!billing_status && (
        <Typography variant='h5' sx={{ color: billing_color, fontWeight: 600 }}>{I18n.t(`billing.status.${billing_status}`)}</Typography>
      )}
    </Box>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 1 }}>
      {!!stripeLink && (
        <Link href={stripeLink} target="_blank" rel="noopener">{I18n.t('quotation.more_detail')}</Link>
      )}
    </Box>
  </Box>

const PriceComponent = ({ value, currency = I18n.t('fiat.eur.sign'), ...rest }) =>
  <TextComponent
    value={_.join([value, currency], ' ')}
    disabled
    {...rest}
  />

const Row1 = ({ load_type, updateBilledHandlingDuration, duration, formatted_effective_handling_duration, passenger_fee }) =>
  <Grid container spacing={3} size={12}>
    <Grid size={3}>
      <TextField
        name={'load_count'}
        label={I18n.t(`load.${load_type}.count`)}
        type='number'
        disabled
      />
    </Grid>
    <Grid size={3}>
      <TextComponent
        label={I18n.t(`passenger.fee`)}
        value={`${passenger_fee} ${I18n.t('fiat.eur.sign')}`}
        disabled
      />
    </Grid>
    <Grid size={3}>
      <TextComponent
        label={I18n.t('quotation.handling_duration')}
        value={formatted_effective_handling_duration}
        afterUserChange={updateBilledHandlingDuration}
        disabled
      />
    </Grid>
    <Grid size={3}>
      <TextComponent
        label={I18n.t('quotation.trip_duration')}
        value={duration || '-'}
        disabled
      />
    </Grid>
  </Grid>

const Row2 = ({ handling, base, voucher_amount, isDurationRideMode, duration_amount }) =>
  <Grid container spacing={3}>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('wait.label', { count: 1 })}
        value={`${handling} ${I18n.t('fiat.eur.sign')}`}
        disabled
      />
    </Grid>
    <Grid size={isDurationRideMode ? 3 : 6}>
      <PriceComponent
        label={I18n.t('quotation.counter_amount_nearing')}
        value={base}
      />
    </Grid >
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('voucher.voucher_amount')}
        value={voucher_amount}
      />
    </Grid >
    <Grid hidden={!isDurationRideMode} size={3}>
      <PriceComponent
        label={I18n.t('ride.duration.amount')}
        value={duration_amount}
      />
    </Grid>
  </Grid >

const Row3 = ({ prepaid, fees, option_amount }) =>
  <Grid container spacing={3} size={12}>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('quotation.fees')}
        value={fees}
      />
    </Grid>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('quotation.prepaid')}
        value={prepaid}
      />
    </Grid>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('options.label', { count: 2 })}
        value={option_amount}
      />
    </Grid>
  </Grid>

const Row4 = ({ driver_discount_amount, customer_discount_amount, driver_discount_percent, customer_discount_percent }) =>
  <Grid container spacing={3} size={12}>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('discount.driver', { discount: driver_discount_percent || '-' })}
        value={driver_discount_amount}
      />
    </Grid>
    <Grid size={4}>
      <PriceComponent
        label={I18n.t('discount.customer', { discount: customer_discount_percent || '-' })}
        value={customer_discount_amount}
      />
    </Grid>
  </Grid>

const Row5 = ({ driver_package_fee, service_amount }) =>
  <Grid container spacing={3} size={12}>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('quotation.duration_driver_package_fee')}
        value={driver_package_fee}
      />
    </Grid>
    <Grid size={3}>
      <PriceComponent
        label={I18n.t('quotation.service_amount')}
        value={service_amount}
      />
    </Grid>
  </Grid>

const Row6 = ({ driver_total, customer_total, displayDownloadBillingButton, handleInvoiceDownload }) =>
  <Box display='flex' flex={1} gap={3} flexDirection="row-reverse" justifyContent='space-between' alignItems='end' mt={2}>
    <Box display='flex' flex={1} flexDirection="column" justifyContent='flex-end'>
      <Typography textAlign='right' fontSize='h6.fontSize' fontWeight={600} color='primary.main'>
        {`${I18n.t('quotation.total.label.driver')}: ${driver_total} ${I18n.t('fiat.eur.sign')}`}
      </Typography>
      <Typography textAlign='right' fontSize='h6.fontSize' fontWeight={600} color='primary.main'>
        <Typography display="inline" color='error' fontSize='inherit' fontWeight='inherit'>*</Typography>
        {`${I18n.t('quotation.total.label.client')}: ${customer_total} ${I18n.t('fiat.eur.sign')}`}
      </Typography>
      <Typography color='error' textAlign='right'>
        {`*${I18n.t('quotation.include_service_amount')}`}
      </Typography>
    </Box>
    {!!displayDownloadBillingButton && (
      <Button
        sx={{ bgcolor: 'primary.main', color: 'white', "&:hover": { color: "primary.main", bgcolor: 'white' } }}
        size='small'
        variant='contained'
        onClick={handleInvoiceDownload}
      >
        <FileDownloadOutlinedIcon />
        <Typography sx={{ ml: 1, fontSize: 'inherit', fontWeight: 600 }}> {I18n.t('quotation.download_invoice')} </Typography>
      </Button>
    )}
  </Box>

const BillingSection = ({ title, row1, row2, row3, row4, row5, row6 }) =>
  <Box sx={{ p: 1, borderRadius: 1, boxShadow: 6 }}>
    <Title {...title} />
    <Box sx={{ p: 1 }}>
      <Grid container rowSpacing={1.5}>
        <Row1 {...row1} />
        <Row2 {...row2} />
        <Row3 {...row3} />
        <Row4 {...row4} />
        <Row5 {...row5} />
        <Row6 {...row6} />
      </Grid>
    </Box>
  </Box>

export default React.memo(BillingSection)
